import { AppBar, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useContext, useState } from 'react';
import * as Colors from '@brightlayer-ui/colors';
import { useHistory } from 'react-router-dom';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { AutoRedirectBanner } from '../AutoRedirectBanner';
import { useSelectedLocationData, RecordDataState, useRefreshLocationList } from '../../hooks/data-hooks';
import { pushAppRoute } from '../../data/domain/route-manager';
import { AppRoute, removeLocationId, addLocationId } from '../../routes';
import { isDataLoaded } from '../../types/data-providers';
import { useOrganizationData } from '../../data/DataProviders/OrganizationDataProvider';
import { sortObjectArrayNaturally } from '../../lib';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';

import GetAppIcon from '@mui/icons-material/GetApp';
import { PopupContext } from '../../data/DataProviders/TimelinePopupProvider';
import { UserMenuToolbar } from '../UserMenuToolbar/UserMenuToolbar';
import { Selectors } from '../../selectors';
import {
    LocationsDropdownAlarmCountProvider,
    useLocationsDropdownAlarmCountData,
} from '../../data/DataProviders/LocationsDropdownAlarmCountProvider';
import { useSignalRData } from '../../data/DataProviders/SignalRDataProvider';
import { SignalRLocation } from '../../types/signalr-types';
import { getUpdatedLocation } from '../../lib/signalr-helper';
import { useSelector } from 'react-redux';
import { DropdownToolbar } from '../DropdownToolbar/DropdownToolbar';

const TIMELINE_TITLE = 'Timeline';
const useStyles = makeStyles({
    error: { background: Colors.red[500], color: Colors.white[50] },
    default: { background: Colors.white[50] },
    downloadWhite: { color: Colors.white[50] },
    downloadDefault: { background: '#ffffff00' },
});
export const LocationsDropdownToolbar: React.FC<{
    title: string;
    shouldIndicateAlarms?: boolean;
    disableDownload?: boolean;
}> = (props) => {
    const organizationData = useOrganizationData();
    const currentAppRoute = useCurrentAppRoute();

    const locationId =
        props.shouldIndicateAlarms && 'locationId' in currentAppRoute ? currentAppRoute.locationId : undefined;

    const organizationId =
        props.shouldIndicateAlarms && isDataLoaded(organizationData) ? organizationData.value.id : undefined;

    return (
        <LocationsDropdownAlarmCountProvider organizationOrLocationId={locationId || organizationId}>
            <InnerLocationsDropdownToolbar //eslint-disable-line @typescript-eslint/no-use-before-define
                title={props.title}
                shouldIndicateAlarms={props.shouldIndicateAlarms}
                disableDownload={props.disableDownload}
            />
        </LocationsDropdownAlarmCountProvider>
    );
};

export const InnerLocationsDropdownToolbar: React.FC<{
    title: string;
    shouldIndicateAlarms?: boolean;
    disableDownload?: boolean;
}> = (props) => {
    const locationListData = useRefreshLocationList(); //this hook used when location need to refresg after create or delete
    const selectedLocationData = useSelectedLocationData();
    const history = useHistory();
    const classes = useStyles();
    const currentAppRoute = useCurrentAppRoute();
    const { t } = useLanguageLocale();
    const popUpAction = useContext(PopupContext);
    const exportLoading = useSelector(Selectors.TimelineExport.timelineExportLoading);
    const alarmCountData = useLocationsDropdownAlarmCountData();
    const signalRData = useSignalRData();
    const [hasAlarm, setAlarm] = useState(false);
    const [disableDownload, setdisableDownload] = useState(false);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const setCurrentLocation = (newLocationId: string | undefined, orgId: string): void => {
        if (!newLocationId) {
            pushAppRoute(history, removeLocationId(currentAppRoute, orgId));
            return;
        }

        const destinationAppRoute: AppRoute | undefined =
            'locationId' in currentAppRoute
                ? {
                      ...currentAppRoute,
                      locationId: newLocationId,
                  }
                : undefined;

        if (destinationAppRoute) {
            pushAppRoute(history, destinationAppRoute);
            return;
        }

        if (newLocationId) {
            pushAppRoute(history, addLocationId(currentAppRoute, newLocationId));
        }
    };

    React.useEffect(() => {
        if (isDataLoaded(locationListData)) {
            return locationListData.value.locations.length > 0 ? setdisableDownload(false) : setdisableDownload(true);
        }
    }, [locationListData]);

    const menuItems =
        isDataLoaded(locationListData) && locationListData.value.locations.length > 0
            ? [
                  {
                      label: t('DASHBOARD.ALL_LOCATIONS'),
                      onClick: (): void => setCurrentLocation(undefined, locationListData.value.organizationId),
                  },
                  ...sortObjectArrayNaturally(locationListData.value.locations, [{ sortField: 'locationName' }]).map(
                      (location) => ({
                          label: location.locationName,
                          onClick: (): void =>
                              setCurrentLocation(location.locationId, locationListData.value.organizationId),
                      })
                  ),
              ]
            : [];

    const getCurrentLocationLabel = (): string | undefined => {
        switch (selectedLocationData.state) {
            case RecordDataState.Loading:
                return undefined;
            case RecordDataState.NotFound:
            case RecordDataState.Error:
                return t('DASHBOARD.CHOOSE_LOCATION');
            case RecordDataState.None:
                return t('DASHBOARD.ALL_LOCATIONS');
            case RecordDataState.Found:
                return selectedLocationData.value.name;
            default:
                return undefined;
            // assertUnreachable(selectedLocationData.state);
        }
    };
    React.useEffect(() => {
        setAlarm(isDataLoaded(alarmCountData) && alarmCountData.value > 0);
    }, [alarmCountData]);
    React.useEffect(() => {
        if (
            props.shouldIndicateAlarms &&
            signalRData.locations.length > 0 &&
            (currentAppRoute.type === 'OverviewLocation' || currentAppRoute.type === 'TimelineLocation')
        ) {
            const updatedLocation: SignalRLocation | undefined = getUpdatedLocation(
                signalRData.locations,
                currentAppRoute.locationId
            );

            if (updatedLocation && updatedLocation.alarmCount !== null) setAlarm(updatedLocation.alarmCount > 0);
        }
    }, [signalRData]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <>
            <AppBar
                data-testid="app-bar"
                position="sticky"
                color="default"
                className={hasAlarm ? classes.error : classes.default}
                style={{ zIndex: 10 }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <DropdownToolbar
                        position="sticky"
                        title={props.title}
                        subtitleLabel={getCurrentLocationLabel()}
                        menuItems={menuItems}
                    />
                    <div style={{ padding: '10px', display: 'flex', justifyContent: 'space-between' }}>
                        {props.title === TIMELINE_TITLE && (
                            <IconButton
                                disabled={exportLoading || disableDownload || props.disableDownload}
                                data-testid="download"
                                onClick={(): void => popUpAction && popUpAction.openPopup(true)}
                                size="large"
                            >
                                <GetAppIcon className={hasAlarm ? classes.downloadWhite : classes.downloadDefault} />
                            </IconButton>
                        )}

                        <UserMenuToolbar />
                    </div>
                </div>
            </AppBar>

            <AutoRedirectBanner />
        </>
    );
};
